/**
 * Reusable horizontally-scrollable category picker - replaces a native
 * <select> for category switching (native selects are the worst-behaved
 * widget for TV-remote/D-pad use - see the TV Guide's original category
 * dropdown this was extracted from). One instance per rail: handles
 * click-to-select, Left/Right roving keyboard focus between chips
 * (plain buttons don't get that for free - only radiogroup/listbox roles
 * do), wheel-to-horizontal-scroll (the rail's native scrollbar is styled
 * subtle, not removed - see main.css - but a plain vertical wheel over a
 * horizontal-only overflow box isn't reliably redirected by every
 * browser), and wiring the .scroll-left/.scroll-right arrow buttons in the
 * closest .scroll-wrapper ancestor (same pattern as Home's Favorite
 * Channels row, HomePage.js initScrollArrows()).
 */
class CategoryRail {
    constructor(rail, { onChange } = {}) {
        this.rail = rail;
        this.onChange = onChange || (() => { });
        this.value = '';
        this.items = [];
        this._updateArrows = null;

        if (!this.rail) return;
        // Chips already get role="tab" + roving-tabindex keyboard nav (see
        // _bindInteractions()) - without this container role, screen readers
        // won't announce the widget as a tablist or expose chip count/
        // position ("tab 2 of 6") even though the keyboard behavior is
        // already correct.
        this.rail.setAttribute('role', 'tablist');
        this._bindInteractions();
        this._bindScrollArrows();
    }

    _bindInteractions() {
        this.rail.addEventListener('click', (e) => {
            const chip = e.target.closest('.category-chip');
            if (!chip) return;
            this.setValue(chip.dataset.value, { fromUser: true });
        });

        this.rail.addEventListener('keydown', (e) => {
            if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
            const chips = [...this.rail.querySelectorAll('.category-chip')];
            const currentIndex = chips.indexOf(document.activeElement);
            if (currentIndex === -1) return;
            e.preventDefault();
            const nextIndex = e.key === 'ArrowRight'
                ? Math.min(chips.length - 1, currentIndex + 1)
                : Math.max(0, currentIndex - 1);
            chips[nextIndex]?.focus();
        });

        this.rail.addEventListener('wheel', (e) => {
            if (e.deltaY === 0) return;
            e.preventDefault();
            this.rail.scrollLeft += e.deltaY;
        }, { passive: false });
    }

    _bindScrollArrows() {
        const wrapper = this.rail.closest('.scroll-wrapper');
        const leftBtn = wrapper?.querySelector('.scroll-left');
        const rightBtn = wrapper?.querySelector('.scroll-right');
        if (!wrapper || !leftBtn || !rightBtn) return;

        const scrollAmount = 200;
        leftBtn.addEventListener('click', () => this.rail.scrollBy({ left: -scrollAmount, behavior: 'smooth' }));
        rightBtn.addEventListener('click', () => this.rail.scrollBy({ left: scrollAmount, behavior: 'smooth' }));

        this._updateArrows = () => {
            const { scrollLeft, scrollWidth, clientWidth } = this.rail;
            leftBtn.classList.toggle('hidden', scrollLeft <= 0);
            rightBtn.classList.toggle('hidden', scrollLeft + clientWidth >= scrollWidth - 5);
        };
        this.rail.addEventListener('scroll', this._updateArrows);
    }

    /**
     * Rebuild the chip list. items: [{ value, label }]. Keeps `preferredValue`
     * (defaults to whatever is currently selected) selected if it's present
     * in the new items, otherwise falls back to the first item (mirrors a
     * native <select> resetting to its first <option> when its options are
     * replaced).
     */
    setItems(items, preferredValue = this.value) {
        if (!this.rail) return;
        this.items = items;
        this.rail.innerHTML = items.map(({ value, label }) => `
            <button type="button" class="category-chip" role="tab"
                    data-value="${escapeHtml(value)}"
                    aria-selected="false"
                    tabindex="-1">${escapeHtml(label)}</button>
        `).join('');
        const stillExists = items.some(i => i.value === preferredValue);
        this._syncSelection(stillExists ? preferredValue : (items[0]?.value ?? ''));
    }

    /**
     * Select a value. Pass { fromUser: true } only for real user
     * interaction (click/keyboard) - programmatic restores (e.g. after
     * setItems()) must not re-trigger onChange.
     */
    setValue(value, { fromUser = false } = {}) {
        this._syncSelection(value);
        if (fromUser) this.onChange(value);
    }

    getValue() {
        return this.value;
    }

    _syncSelection(value) {
        this.value = value;
        if (!this.rail) return;
        const chips = this.rail.querySelectorAll('.category-chip');
        let activeChip = null;
        chips.forEach(chip => {
            const isActive = chip.dataset.value === value;
            chip.classList.toggle('active', isActive);
            chip.setAttribute('aria-selected', String(isActive));
            chip.tabIndex = isActive ? 0 : -1;
            if (isActive) activeChip = chip;
        });
        activeChip?.scrollIntoView({ inline: 'nearest', block: 'nearest' });

        // Chip count/widths may have just changed (setItems rebuild) -
        // re-check after layout/scroll-into-view settle, same as Home's
        // own setTimeout(updateArrows, 100) on initial load.
        setTimeout(() => this._updateArrows?.(), 100);
    }
}

window.CategoryRail = CategoryRail;
